import Decimal from 'decimal.js';

import { CURRENCY, LOCALE, PLACEHOLDER } from './consts/strings';
import { parseNumber } from './parseNumber';

const formatNumber = (dividend, divisor) =>
  Number(new Decimal(dividend / divisor).toFixed(2).toString());

/**
 * https://git.io/v7PD5 - bostock copypasta
 * number conversion and back to string strips insignificant trailing zeroes
 */

const formatBillion = x => formatNumber(x, 1e9) + 'B';
const formatMillion = x => formatNumber(x, 1e6) + 'MM';
const formatThousand = x => formatNumber(x, 1e3) + 'k';

function abbreviateValue(x) {
  const value = Math.abs(x);

  if (value >= 0.9995e9) return formatBillion(value);
  if (value >= 0.9995e6) return formatMillion(value);

  return formatThousand(value);
}

/**
 * formatCurrency
 * Stylize number values displayed as a string
 *
 * @param {*} amount
 * @param {Object} [options]
 * @param {Boolean} [options.abbreviate=false] - display with letter abbreviatons
 * @param {Boolean} [options.currencySymbol=true] - show/hide currency symbol
 * @param {'parenthetical'|'minus'|'disabled'} [options.negativeNotation=parenthetical] - consumer or professional notation
 * @param {'none'|'plus'} [options.positiveNotation=none] - emphasis of positive values
 * @param {Boolean} [options.showPlaceholder=true] - display placeholder instead of 0, null, or undefined
 * @param {Boolean} [options.showZero=false] - display 0 instead of placeholder
 * @param {Boolean} [options.stripTrailingZeros=true] - display integer only when value is whole
 */

export type FormatCurrencyOptions = {
  abbreviate?: boolean;
  currencySymbol?: boolean;
  negativeNotation?: 'minus' | 'parenthetical' | 'disabled';
  positiveNotation?: 'none' | 'plus';
  showPlaceholder?: boolean;
  showZero?: boolean;
  stripTrailingZeros?: boolean;
} & Intl.NumberFormatOptions;

export function formatCurrency(
  value: number | string | Decimal,
  options?: FormatCurrencyOptions
): string {
  const opts = {
    abbreviate: false,
    currency: CURRENCY,
    currencySymbol: true,
    negativeNotation: 'minus',
    positiveNotation: 'none',
    showPlaceholder: true,
    showZero: false,
    stripTrailingZeros: true,
    style: 'currency' as const,
    ...options
  };

  if (value === null || value === undefined) {
    return value as string;
  }

  const amount = parseNumber(value);

  if (amount === 0 && opts.showPlaceholder && !opts.showZero) {
    return PLACEHOLDER;
  }

  if (typeof amount !== 'number') {
    return amount;
  }

  let currency = Number(amount).toLocaleString(LOCALE, {
    currency: opts.currency,
    currencyDisplay: opts.currencyDisplay,
    currencySign: opts.currencySign,
    localeMatcher: opts.localeMatcher,
    maximumFractionDigits: opts.maximumFractionDigits,
    maximumSignificantDigits: opts.maximumSignificantDigits,
    minimumFractionDigits: opts.minimumFractionDigits,
    minimumIntegerDigits: opts.minimumIntegerDigits,
    minimumSignificantDigits: opts.minimumSignificantDigits,
    roundingMode: opts.roundingMode,
    style: opts.style,
    useGrouping: opts.useGrouping
  });

  if (opts.abbreviate && amount > 0) {
    currency = '$' + abbreviateValue(amount);
  } else if (opts.abbreviate) {
    currency = '-$' + abbreviateValue(amount);
  }

  if (amount >= 0 && opts.positiveNotation === 'plus') {
    currency = `+${currency}`;
  }

  if (amount < 0 && opts.negativeNotation === 'parenthetical') {
    currency = currency.replace('-$', '($').concat(')');
  } else if (amount < 0 && opts.negativeNotation === 'minus') {
    currency = `-${currency.replace('-', '')}`;
  } else if (amount < 0 && opts.negativeNotation === 'disabled') {
    currency = `${currency.replace('-', '')}`;
  }

  if (!opts.currencySymbol) currency = currency.replace('$', '');

  if (currency.includes('.00') && opts.stripTrailingZeros) {
    return currency.replace('.00', '');
  }

  return currency;
}
